// src/pages/Dashboard/ChatbotScripts.js
import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import axiosInstance from "../../../utils/axiosInstance";
import { ChatScriptsURL, CHATBOT_UI } from "../../../config";
import { useChatbots } from "../../../contexts/ChatbotContext";
import { Box, Typography, TextField, Button, CircularProgress, Alert, Snackbar } from "@mui/material";
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import CheckIcon from '@mui/icons-material/Check';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  accent: '#3b82f6',
};

export default function ChatbotScripts() {
  const { activeChatbot } = useChatbots();
  const [error, setError] = useState(null);
  const [scriptFilename, setScriptFilename] = useState("");
  const [scriptData, setScriptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { chatbotId: paramsChatbotId } = useParams();

  useEffect(() => {
    if (activeChatbot) fetchData();
  }, [activeChatbot]);

  const fetchData = async () => {
    if (!activeChatbot) {
      setError("No active chatbot selected.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = { chatbot_id: paramsChatbotId };
      const response = await axiosInstance.post(`/get-javascript`, {}, { params });
      const responseData = response.data.data;
      if (Object.keys(responseData).length > 0) setScriptData(responseData);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch script.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (scriptData?.chatbot_identity) {
      const dynamicScript = `<!-- Add in the <head> section -->
<script src="${ChatScriptsURL}/embed/klonit-chatbot.js"></script>
<script>
   function initializeChatbot() {
      if (window.KloneIT && typeof window.KloneIT.init === 'function') {
         const config = {CHATBOT_ID: '${scriptData.chatbot_identity}'};
         window.KloneIT.init(config);
      } else {setTimeout(initializeChatbot, 100);}
   }
   if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeChatbot);
   } else {initializeChatbot();}
</script>`;
      setScriptFilename(dynamicScript);
    } else setScriptFilename("");
  }, [scriptData]);

  const handleCopyClick = () => {
    navigator.clipboard.writeText(scriptFilename)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error("Copy failed", err));
  };

  const chatbotLink = scriptData ? `${CHATBOT_UI}/?CHATBOT_ID=${scriptData.chatbot_identity}` : "#";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
        <CircularProgress size={24} sx={{ color: colors.accent }} />
        <Typography sx={{ ml: 2, fontFamily, fontSize: '0.9rem', color: colors.textSecondary }}>
          Loading script...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 36,
          height: 36,
          borderRadius: '10px',
          bgcolor: 'rgba(59,130,246,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CodeIcon sx={{ fontSize: '1.2rem', color: colors.accent }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 600, color: colors.textPrimary }}>
            Embed Script
          </Typography>
          <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
            Add this script to your website
          </Typography>
        </Box>
      </Box>

      {/* Script Textarea */}
      <TextField
        value={scriptFilename}
        multiline
        fullWidth
        rows={8}
        InputProps={{ readOnly: true }}
        placeholder="Select a chatbot to generate its script."
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            borderRadius: '12px',
            bgcolor: 'rgba(30,41,59,0.03)',
            '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
            '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.15)' },
            '&.Mui-focused fieldset': { borderColor: colors.accent },
          },
        }}
      />

      {/* Action Buttons */}
      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <Button
          variant="contained"
          onClick={handleCopyClick}
          disabled={!scriptFilename}
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          sx={{
            fontFamily,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            borderRadius: '10px',
            px: 2.5,
            py: 1,
            background: copied ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            boxShadow: copied ? '0 4px 12px rgba(16,185,129,0.3)' : '0 4px 12px rgba(59,130,246,0.3)',
            '&:hover': {
              background: copied ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
            },
          }}
        >
          {copied ? 'Copied!' : 'Copy Script'}
        </Button>
        <Button
          variant="outlined"
          href={chatbotLink}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon />}
          sx={{
            fontFamily,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.85rem',
            borderRadius: '10px',
            px: 2.5,
            py: 1,
            borderColor: 'rgba(0,0,0,0.15)',
            color: colors.textSecondary,
            '&:hover': { borderColor: colors.accent, color: colors.accent },
          }}
        >
          Open Chatbot
        </Button>
        <Button
          variant="outlined"
          href="wordpress-plugin"
          startIcon={<DownloadIcon />}
          sx={{
            fontFamily,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.85rem',
            borderRadius: '10px',
            px: 2.5,
            py: 1,
            borderColor: 'rgba(0,0,0,0.15)',
            color: colors.textSecondary,
            '&:hover': { borderColor: colors.accent, color: colors.accent },
          }}
        >
          WordPress Plugin
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mt: 2, fontFamily, borderRadius: '10px' }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
